import {Component, OnInit, QueryList, ViewChildren} from '@angular/core';
import {ModalService} from '../../../../../shared/modals/modal/modal.service';
import {SchoolAdminGroupService} from '../../../../../services/school/school-admin-group.service';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {
  GroupModel,
  GroupQueryModel,
  MigrationStatus,
  MIGRATION_STATUSES_NEEDING_ATTENTION,
  MIGRATION_STATUSES_NEEDING_REVIEW,
  GroupTypes
} from '../../../../../models/school/group.model';
import {ErrorModel} from '../../../../../models/shared/error.model';
import {TranslateService} from '@ngx-translate/core';
import {StudentService} from '../../../../../services/student/student.service';
import {StudentModel} from '../../../../../models/authentication/student.model';
import {QrCodeDownloadService} from '../../../../../services/user/qr-code-download.service';
import {DropdownModel} from '../../../../../models/component/dropdown.model';
import {EvoGroupTypeModalService} from '../../../../../shared/modals/evo-group-type-modal/evo-group-type-modal.service';
import {SchoolAdminGroupObjectiveChangeService} from '../../shared/school-admin-group-objective-change/school-admin-group-objective-change.service';

@Component({
  selector: 'app-school-admin-group-detail-settings',
  templateUrl: './school-admin-group-detail-settings.component.html',
  styleUrls: ['./school-admin-group-detail-settings.component.css']
})
export class SchoolAdminGroupDetailSettingsComponent implements OnInit {

  @ViewChildren('qrcodes') qrcodes: QueryList<any>;

  groupId: string;
  group: GroupModel;
  groupName = '';
  password = '';

  GroupTypes = GroupTypes;

  currentGroupType = this.GroupTypes.standard;

  isResolvingMigration = false;

  groupFilter = new GroupQueryModel();
  students: StudentModel[] = [];

  currentGrade: number = null;

  isDownloadingAllStudents = false;
  shouldDownloadQRCodes = false;

  constructor(private modalSv: ModalService,
              private route: ActivatedRoute,
              private router: Router,
              private tSv: TranslateService,
              private studentSv: StudentService,
              private qrCodeDownloadSv: QrCodeDownloadService,
              private groupSv: SchoolAdminGroupService,
              private groupTypeModalSv: EvoGroupTypeModalService,
              public objectiveChangeSv: SchoolAdminGroupObjectiveChangeService) { }

  ngOnInit() {

    this.route.parent.params.subscribe((params: Params) => {

      this.groupId = params.groupId;
      this.groupSv.fetchGroup(this.groupId, true).subscribe((group) => {

        this.group = group;
        this.groupName = group.name;
        this.currentGrade = group.grade !== undefined ? group.grade : null;
        this.currentGroupType = group.groupType || GroupTypes.standard;

      });

      this.isDownloadingAllStudents = true;
      this.fetchGroupStudents();

    });

  }

  getGradeDropdown(): DropdownModel[] {

    const list = [];
    list.push(new DropdownModel(null, 'reusable.none'));

    const gradeKeys = [
      'schoolAdministration.groups.grade0',
      'schoolAdministration.groups.grade1',
      'schoolAdministration.groups.grade2',
      'schoolAdministration.groups.grade3',
      'schoolAdministration.groups.grade4',
      'schoolAdministration.groups.grade5',
      'schoolAdministration.groups.grade6',
      'schoolAdministration.groups.grade7',
      'schoolAdministration.groups.grade8',
      'schoolAdministration.groups.grade9',
      'schoolAdministration.groups.grade10',
      'schoolAdministration.groups.grade11',
      'schoolAdministration.groups.grade12',
    ];

    gradeKeys.forEach((key, index) => {
      list.push(new DropdownModel(index.toString(10), this.tSv.instant(key)));
    });

    return list;

  }

  onGradeSelected(grade: string) {
    this.currentGrade = parseInt(grade, 10);
  }

  onGradeString(): string {

    if (this.currentGrade != null) {
      return this.currentGrade.toString(10);
    }

    return null;
  }

  canSaveGrade(): boolean {

    if (!this.group) {
      return false;
    }

    const savedGrade = this.group.grade !== undefined ? this.group.grade : null;
    return this.currentGrade !== savedGrade;

  }

  onSaveGradeClicked() {

    this.groupSv.updateGroupGrade(this.groupId, this.currentGrade).subscribe((group) => {

      this.group = group;
      this.currentGrade = null;
      if (group.grade != null) {
        this.currentGrade = group.grade;
      }
      this.modalSv.showAlertModal(this.tSv.instant('reusable.success'), this.tSv.instant('reusable.successMessage'));

    }, (err: ErrorModel) => {

      this.modalSv.showTranslatedErrorModal(err);

    });

  }

  onChangeGroupTypeClicked() {
    this.groupTypeModalSv.show(this.currentGroupType).subscribe((selectedType) => {
      if (!selectedType) { return; }
      this.groupSv.updateGroupType(this.groupId, selectedType).subscribe((group) => {
        this.group = group;
        this.currentGroupType = group.groupType || GroupTypes.standard;
      }, (err: ErrorModel) => {
        this.modalSv.showTranslatedErrorModal(err);
      });
    });
  }

  fetchGroupStudents() {

    this.groupFilter.limit = 50;
    this.groupFilter.skip = this.students.length;
    this.groupSv.fetchGroupStudents(this.groupFilter, this.groupId).subscribe((students) => {

      this.students = this.students.concat(students);

      if (students.length === this.groupFilter.limit) {
        this.fetchGroupStudents();
      } else {
        this.isDownloadingAllStudents = false;
        if (this.shouldDownloadQRCodes === true) {
          this.shouldDownloadQRCodes = false;
          this.onDownloadQRCodes();
          this.modalSv.cancelClicked();
        }
      }

    });

  }

  hasActivatedQRCodes(): boolean {

    let activated = true;
    for (const s of this.students) {
      if (s.isQRCodeAuthEnabled === undefined || !s.qrCodePassword || s.isQRCodeAuthEnabled === false) {
        activated = false;
      }
    }

    return activated;

  }

  qrCodeActivationChanged(enabled: boolean) {

    this.groupSv.updateGroupQRCodeAuth(this.group, enabled).subscribe((value) => {

      this.groupFilter.skip = 0;
      this.students = [];
      this.fetchGroupStudents();

    });
  }

  // https://stackoverflow.com/questions/41484323/how-to-use-jspdf-with-angular-2
  onDownloadQRCodes() {

    if (this.isDownloadingAllStudents === true) {
      this.shouldDownloadQRCodes = true;
      // Show modal
      this.modalSv.showPopUp(this.tSv.instant('reusable.loading'), this.tSv.instant('schoolAdministration.groups.loadingQRCodes'));
      return ;
    }

    this.qrCodeDownloadSv.onDownloadQRCodes(this.qrcodes, this.students, this.group);

  }

  needsMigrationAttention(): boolean {
    if (!this.group) { return false; }
    return MIGRATION_STATUSES_NEEDING_ATTENTION.indexOf(this.group.migrationStatus as MigrationStatus) !== -1;
  }

  needsMigrationReview(): boolean {
    if (!this.group) { return false; }
    return MIGRATION_STATUSES_NEEDING_REVIEW.includes(this.group.migrationStatus as MigrationStatus);
  }

  needsMigrationResolved(): boolean {
    if (!this.group) { return false; }
    return this.group.migrationStatus === 'RESOLVED';
  }

  needsMigrationCard(): boolean {
    return this.needsMigrationAttention() || this.needsMigrationReview() || this.needsMigrationResolved();
  }

  getMigrationGradeKey(): string {
    if (!this.group || this.group.grade == null) { return ''; }
    return 'schoolAdministration.groups.grade' + this.group.grade;
  }

  getMigrationGroupTypeKey(): string {
    if (!this.group) { return ''; }
    const map: any = {
      standard: 'schoolAdministration.groups.groupTypeStandard',
      mixed: 'schoolAdministration.groups.groupTypeMixed',
      specialEducation: 'schoolAdministration.groups.groupTypeSpecial',
    };
    return map[this.group.groupType] || '';
  }

  canResolve(): boolean {
    return this.group && this.group.migrationStatus !== 'NO_OBJECTIVE';
  }

  getMigrationTitleKey(): string {
    if (!this.group) { return ''; }
    const map: any = {
      NO_OBJECTIVE: 'schoolAdministration.groups.migrationCardNoObjectiveTitle',
      OBJECTIVE_MISMATCH_LOWER: 'schoolAdministration.groups.migrationCardMismatchLowerTitle',
      AT_MAX_OBJECTIVE: 'schoolAdministration.groups.migrationCardAtMaxTitle',
      MIXED_GROUP_NOT_ADVANCED: 'schoolAdministration.groups.migrationCardMixedTitle',
      OBJECTIVE_ADVANCED: 'schoolAdministration.groups.migrationCardObjectiveAdvancedTitle',
      OBJECTIVE_CONFIRMED: 'schoolAdministration.groups.migrationCardObjectiveConfirmedTitle',
      NON_STANDARD_GROUP: 'schoolAdministration.groups.migrationCardNonStandardGroupTitle',
    };
    return map[this.group.migrationStatus] || '';
  }

  getMigrationDescKey(): string {
    if (!this.group) { return ''; }
    const map: any = {
      NO_OBJECTIVE: 'schoolAdministration.groups.migrationCardNoObjectiveDesc',
      OBJECTIVE_MISMATCH_LOWER: 'schoolAdministration.groups.migrationCardMismatchLowerDesc',
      AT_MAX_OBJECTIVE: 'schoolAdministration.groups.migrationCardAtMaxDesc',
      MIXED_GROUP_NOT_ADVANCED: 'schoolAdministration.groups.migrationCardMixedDesc',
      OBJECTIVE_ADVANCED: 'schoolAdministration.groups.migrationCardObjectiveAdvancedDesc',
      OBJECTIVE_CONFIRMED: 'schoolAdministration.groups.migrationCardObjectiveConfirmedDesc',
      NON_STANDARD_GROUP: 'schoolAdministration.groups.migrationCardNonStandardGroupDesc',
    };
    return map[this.group.migrationStatus] || '';
  }

  onObjectiveUpdated(group: GroupModel) {
    const wasNoObjective = this.group && this.group.migrationStatus === 'NO_OBJECTIVE';
    this.group = group;
    if (wasNoObjective) {
      this.groupSv.fetchGroup(this.groupId, true).subscribe((refreshed) => {
        this.group = refreshed;
      });
    }
  }

  onResolveMigrationClicked() {
    this.isResolvingMigration = true;
    this.groupSv.resolveGroupContext(this.groupId).subscribe(() => {
      this.group.migrationStatus = 'RESOLVED';
      this.isResolvingMigration = false;
    }, (err: ErrorModel) => {
      this.isResolvingMigration = false;
      this.modalSv.showTranslatedErrorModal(err);
    });
  }

  canSaveGroupName():  boolean {

    if (this.group) {
      if (this.group.name !== this.groupName) {
        return true;
      }
    }

    return false;
  }

  onSaveGroupNameClicked() {

    const previousGroupName = this.group.name;

    this.group.name = this.groupName;
    this.groupSv.updateGroup(this.group).subscribe((group) => {

      this.group = group;
      this.groupName = group.name;
      this.router.navigate(['schooladmin', 'groups', group._id, 'settings']);

    }, (err: ErrorModel) => {

      this.group.name = previousGroupName;
      this.modalSv.showTranslatedErrorModal(err);

    });

  }

  canDeleteGroup(): boolean {

    if (this.password.length >= 6) {

      return true;
    }

    return false;
  }

  onDeleteGroupClicked() {

    this.modalSv.showChoiceModal(this.tSv.instant('reusable.warning'),
      this.tSv.instant('schoolAdministration.groups.deleteGroupModalWarning'))
      .subscribe((response) => {

        if (response) {

          this.groupSv.deleteGroup(this.groupId, this.password).subscribe((group) => {

            this.modalSv.showAlertModal(this.tSv.instant('resuable.success'),
              this.tSv.instant('reusable.successMessage'))
              .subscribe(() => {

                this.router.navigate(['schooladmin', 'groups']);

              });

          }, (err: ErrorModel) => {

            this.modalSv.showTranslatedErrorModal(err);

          });
        }
      });

  }

}
